import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Ban, Loader2, Trash2, Pencil, Check, X, Search, ChevronLeft, ChevronRight, Download, History, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

interface BlacklistedDomain {
  id: string;
  domain: string;
  reason: string | null;
  created_at: string;
}

interface AuditEntry {
  id: string;
  action: string; // 'blacklist_domain_added' | 'blacklist_domain_removed' | 'blacklist_domain_updated'
  created_at: string;
  user_id: string | null;
  details: { domain?: string; reason?: string | null; previous_domain?: string; previous_reason?: string | null } | null;
  actor_email?: string | null;
}

const normalize = (raw: string) =>
  raw.trim().toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, "").replace(/^@/, "").replace(/\/.*$/, "");

const isValidDomain = (d: string) => /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(d);

type SupaError = { code?: string; message?: string; details?: string; hint?: string };

const isNetworkError = (err: unknown) =>
  err instanceof TypeError && /fetch|network|failed to fetch/i.test(err.message);

const describeError = (err: unknown, action: string, domain?: string): string => {
  if (isNetworkError(err)) {
    return `Network error while ${action}. Check your connection and try again.`;
  }
  const e = (err ?? {}) as SupaError;
  const code = e.code ?? "";
  const msg = e.message ?? "";

  if (code === "23505" || /duplicate|unique/i.test(msg)) {
    return domain ? `"${domain}" is already on the blacklist.` : "That domain is already on the blacklist.";
  }
  if (code === "42501" || /row-level security|permission denied|not authorized/i.test(msg)) {
    return `Permission denied while ${action}. You must be an admin.`;
  }
  if (code === "23514" || /violates check constraint|invalid input/i.test(msg)) {
    return `Invalid value while ${action}. Please check the domain format.`;
  }
  if (code === "PGRST301" || /jwt|expired|not authenticated/i.test(msg)) {
    return "Your session has expired. Please sign in again.";
  }
  return msg ? `Failed to ${action}: ${msg}` : `Failed to ${action}. Please try again.`;
};

// Console + activity_logs (non-blocking) for raw error visibility
const logRawError = (context: string, err: unknown, extra?: Record<string, unknown>) => {
  // eslint-disable-next-line no-console
  console.error(`[BlacklistManager] ${context}`, err, extra ?? {});
  setTimeout(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const e = (err ?? {}) as SupaError;
      await supabase.from("activity_logs").insert({
        user_id: session?.user?.id ?? null,
        action_category: "admin",
        action_type: `blacklist.${context}`,
        success: false,
        error_message: e.message ?? (err instanceof Error ? err.message : String(err)),
        details: { code: e.code ?? null, hint: e.hint ?? null, details: e.details ?? null, ...(extra ?? {}) } as never,
      });
    } catch {
      /* swallow logging failures */
    }
  }, 0);
};

const writeAuditLog = (action: string, details: Record<string, unknown>) => {
  setTimeout(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      await supabase.from("admin_logs").insert({
        user_id: session.user.id,
        action,
        entity_type: "blacklisted_domain",
        details: details as never,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[BlacklistManager] failed to write audit log", err);
    }
  }, 0);
};

const PAGE_SIZE = 10;

export const BlacklistManager = () => {
  const [domains, setDomains] = useState<BlacklistedDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newDomain, setNewDomain] = useState("");
  const [newReason, setNewReason] = useState("");
  const [addError, setAddError] = useState<{ field: "domain" | "reason" | "form"; message: string } | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDomain, setEditDomain] = useState("");
  const [editReason, setEditReason] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState<{ field: "domain" | "reason" | "form"; message: string } | null>(null);

  const [removingId, setRemovingId] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => { setPage(1); }, [search]);

  const filteredDomains = domains.filter((d) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return d.domain.toLowerCase().includes(q) || (d.reason ?? "").toLowerCase().includes(q);
  });
  const totalPages = Math.max(1, Math.ceil(filteredDomains.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedDomains = filteredDomains.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("blacklisted_domains")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Failed to load blacklist");
      logRawError("load", error);
    }
    setDomains((data ?? []) as BlacklistedDomain[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Re-check duplicates against fresh server state right before mutating
  const checkDuplicateLive = async (domain: string, ignoreId?: string): Promise<boolean> => {
    const { data, error } = await supabase
      .from("blacklisted_domains")
      .select("id")
      .eq("domain", domain)
      .limit(1);
    if (error) {
      logRawError("duplicate_check", error, { domain });
      return false; // fall through; insert will surface 23505 if it exists
    }
    return (data ?? []).some((row) => row.id !== ignoreId);
  };

  const addDomainAttempt = useCallback(
    async (domain: string, reason: string): Promise<{ ok: boolean; error?: unknown }> => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return { ok: false, error: { code: "PGRST301", message: "Not authenticated" } };

        if (await checkDuplicateLive(domain)) {
          return { ok: false, error: { code: "23505", message: "duplicate" } };
        }

        const { error } = await supabase
          .from("blacklisted_domains")
          .insert({ domain, reason: reason || null, created_by: user.id });
        if (error) return { ok: false, error };

        writeAuditLog("blacklist_domain_added", { domain, reason: reason || null });
        return { ok: true };
      } catch (err) {
        return { ok: false, error: err };
      }
    },
    []
  );

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);
    const domain = normalize(newDomain);
    const reason = newReason.trim();

    if (!domain || !isValidDomain(domain)) {
      setAddError({ field: "domain", message: "Enter a valid domain (e.g. example.com)" });
      return;
    }
    if (domains.some((d) => d.domain === domain)) {
      setAddError({ field: "domain", message: `"${domain}" is already on the blacklist` });
      return;
    }

    setAdding(true);
    const result = await addDomainAttempt(domain, reason);
    setAdding(false);

    if (result.ok) {
      toast.success(`Blacklisted ${domain}. Matching accounts disabled.`);
      setNewDomain("");
      setNewReason("");
      setAddError(null);
      load();
      return;
    }

    logRawError("add", result.error, { domain });
    const msg = describeError(result.error, "adding domain", domain);
    setAddError({ field: "form", message: msg });

    if (isNetworkError(result.error)) {
      toast.error(msg, {
        action: {
          label: "Try again",
          onClick: async () => {
            setAdding(true);
            const retry = await addDomainAttempt(domain, reason);
            setAdding(false);
            if (retry.ok) {
              toast.success(`Blacklisted ${domain}. Matching accounts disabled.`);
              setNewDomain("");
              setNewReason("");
              setAddError(null);
              load();
            } else {
              logRawError("add_retry", retry.error, { domain });
              toast.error(describeError(retry.error, "adding domain", domain));
            }
          },
        },
      });
    } else {
      toast.error(msg);
    }
  };

  const startEdit = (d: BlacklistedDomain) => {
    setEditingId(d.id);
    setEditDomain(d.domain);
    setEditReason(d.reason ?? "");
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDomain("");
    setEditReason("");
    setEditError(null);
  };

  const saveEditAttempt = async (
    id: string,
    domain: string,
    reason: string
  ): Promise<{ ok: boolean; error?: unknown; previous?: BlacklistedDomain }> => {
    try {
      const previous = domains.find((d) => d.id === id);
      if (await checkDuplicateLive(domain, id)) {
        return { ok: false, error: { code: "23505", message: "duplicate" } };
      }
      const { error } = await supabase
        .from("blacklisted_domains")
        .update({ domain, reason: reason || null })
        .eq("id", id);
      if (error) return { ok: false, error };

      writeAuditLog("blacklist_domain_updated", {
        domain,
        reason: reason || null,
        previous_domain: previous?.domain,
        previous_reason: previous?.reason ?? null,
      });
      return { ok: true, previous };
    } catch (err) {
      return { ok: false, error: err };
    }
  };

  const saveEdit = async (id: string, originalDomain: string) => {
    setEditError(null);
    const domain = normalize(editDomain);
    const reason = editReason.trim();

    if (!domain || !isValidDomain(domain)) {
      setEditError({ field: "domain", message: "Enter a valid domain (e.g. example.com)" });
      return;
    }
    if (domain !== originalDomain && domains.some((d) => d.domain === domain && d.id !== id)) {
      setEditError({ field: "domain", message: `"${domain}" is already on the blacklist` });
      return;
    }

    setSavingEdit(true);
    const result = await saveEditAttempt(id, domain, reason);
    setSavingEdit(false);

    if (result.ok) {
      toast.success(domain !== originalDomain ? `Updated to ${domain}` : "Reason updated");
      cancelEdit();
      load();
      return;
    }

    logRawError("edit", result.error, { id, domain });
    const msg = describeError(result.error, "saving changes", domain);
    setEditError({ field: "form", message: msg });

    if (isNetworkError(result.error)) {
      toast.error(msg, {
        action: {
          label: "Try again",
          onClick: async () => {
            setSavingEdit(true);
            const retry = await saveEditAttempt(id, domain, reason);
            setSavingEdit(false);
            if (retry.ok) {
              toast.success(domain !== originalDomain ? `Updated to ${domain}` : "Reason updated");
              cancelEdit();
              load();
            } else {
              logRawError("edit_retry", retry.error, { id, domain });
              toast.error(describeError(retry.error, "saving changes", domain));
            }
          },
        },
      });
    } else {
      toast.error(msg);
    }
  };

  const removeAttempt = async (id: string): Promise<{ ok: boolean; error?: unknown }> => {
    try {
      const { error } = await supabase.from("blacklisted_domains").delete().eq("id", id);
      if (error) return { ok: false, error };
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err };
    }
  };

  const handleRemove = async (id: string, domain: string) => {
    if (!confirm(`Remove ${domain} from blacklist? Existing disabled accounts will remain disabled until manually re-enabled.`)) return;
    const removed = domains.find((d) => d.id === id);

    setRemovingId(id);
    const result = await removeAttempt(id);
    setRemovingId(null);

    if (result.ok) {
      writeAuditLog("blacklist_domain_removed", { domain, reason: removed?.reason ?? null });
      load();
      toast.success(`Removed ${domain}`, {
        action: removed
          ? {
              label: "Undo",
              onClick: async () => {
                try {
                  const { data: { user } } = await supabase.auth.getUser();
                  if (!user) {
                    toast.error("You must be signed in to undo.");
                    return;
                  }
                  const { error: insertErr } = await supabase
                    .from("blacklisted_domains")
                    .insert({ domain: removed.domain, reason: removed.reason, created_by: user.id });
                  if (insertErr) {
                    logRawError("undo_remove", insertErr, { domain: removed.domain });
                    toast.error(describeError(insertErr, "restoring domain", removed.domain));
                  } else {
                    writeAuditLog("blacklist_domain_added", {
                      domain: removed.domain,
                      reason: removed.reason,
                      restored: true,
                    });
                    toast.success(`Restored ${removed.domain}`);
                    load();
                  }
                } catch (err) {
                  logRawError("undo_remove", err, { domain: removed.domain });
                  toast.error(describeError(err, "restoring domain", removed.domain));
                }
              },
            }
          : undefined,
      });
      return;
    }

    logRawError("remove", result.error, { id, domain });
    const msg = describeError(result.error, `removing ${domain}`);
    if (isNetworkError(result.error)) {
      toast.error(msg, {
        action: {
          label: "Try again",
          onClick: async () => {
            setRemovingId(id);
            const retry = await removeAttempt(id);
            setRemovingId(null);
            if (retry.ok) {
              writeAuditLog("blacklist_domain_removed", { domain, reason: removed?.reason ?? null });
              toast.success(`Removed ${domain}`);
              load();
            } else {
              logRawError("remove_retry", retry.error, { id, domain });
              toast.error(describeError(retry.error, `removing ${domain}`));
            }
          },
        },
      });
    } else {
      toast.error(msg);
    }
  };

  const handleExport = () => {
    if (domains.length === 0) {
      toast.error("No domains to export");
      return;
    }
    const escape = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const rows = [
      ["Domain", "Reason", "Created At"].join(","),
      ...domains.map((d) =>
        [escape(d.domain), escape(d.reason ?? ""), escape(new Date(d.created_at).toISOString())].join(",")
      ),
    ];
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `blacklisted-domains-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${domains.length} domain${domains.length === 1 ? "" : "s"}`);
  };

  return (
    <>
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5" /> Account Blacklist
          </CardTitle>
          <CardDescription>
            Block sign-up and sign-in for any email matching these domains (including subdomains).
            Existing accounts on a blacklisted domain are automatically disabled.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={handleAdd} className="space-y-2" noValidate>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="sm:w-56 space-y-1">
                <Input
                  placeholder="domain.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  disabled={adding}
                  aria-invalid={addError?.field === "domain" || addError?.field === "form" ? true : undefined}
                  aria-describedby={addError?.field === "domain" ? "add-domain-error" : undefined}
                />
                {addError?.field === "domain" && (
                  <p id="add-domain-error" className="text-xs text-destructive">{addError.message}</p>
                )}
              </div>
              <div className="flex-1 space-y-1">
                <Input
                  placeholder="Reason (optional)"
                  value={newReason}
                  onChange={(e) => setNewReason(e.target.value)}
                  disabled={adding}
                  aria-invalid={addError?.field === "reason" ? true : undefined}
                  aria-describedby={addError?.field === "reason" ? "add-reason-error" : undefined}
                />
                {addError?.field === "reason" && (
                  <p id="add-reason-error" className="text-xs text-destructive">{addError.message}</p>
                )}
              </div>
              <Button type="submit" disabled={adding || !newDomain.trim()}>
                {adding ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Saving…
                  </span>
                ) : (
                  "Add to blacklist"
                )}
              </Button>
            </div>

            {addError?.field === "form" && (
              <p className="text-xs text-destructive" role="alert">{addError.message}</p>
            )}

            {newDomain.trim() && (() => {
              const preview = normalize(newDomain);
              const valid = isValidDomain(preview);
              const changed = preview !== newDomain.trim().toLowerCase();
              return (
                <p className="text-xs text-muted-foreground">
                  Will be saved as:{" "}
                  <code className={`px-1.5 py-0.5 rounded bg-muted font-mono ${valid ? "text-foreground" : "text-destructive"}`}>
                    {preview || "(empty)"}
                  </code>
                  {valid && changed && <span className="ml-2">— normalized from your input</span>}
                  {!valid && preview && <span className="ml-2 text-destructive">— not a valid domain</span>}
                </p>
              );
            })()}
          </form>

          {!loading && domains.length > 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search domains or reasons"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button variant="outline" size="sm" onClick={handleExport} className="gap-2 sm:ml-auto">
                <Download className="h-4 w-4" /> Export CSV
              </Button>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : domains.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No blacklisted domains yet.</p>
          ) : filteredDomains.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No domains match "{search}".</p>
          ) : (
            <div className="space-y-2">
              {pagedDomains.map((d) => {
                const isEditing = editingId === d.id;
                const isRemoving = removingId === d.id;
                return (
                  <div key={d.id} className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-3 border rounded-md px-3 py-2">
                    {isEditing ? (
                      <>
                        <div className="sm:w-56 space-y-1">
                          <Input
                            value={editDomain}
                            onChange={(e) => setEditDomain(e.target.value)}
                            disabled={savingEdit}
                            placeholder="domain.com"
                            aria-invalid={editError?.field === "domain" || editError?.field === "form" ? true : undefined}
                          />
                          {editError?.field === "domain" && (
                            <p className="text-xs text-destructive">{editError.message}</p>
                          )}
                        </div>
                        <div className="flex-1 space-y-1">
                          <Input
                            value={editReason}
                            onChange={(e) => setEditReason(e.target.value)}
                            disabled={savingEdit}
                            placeholder="Reason (optional)"
                          />
                          {editError?.field === "form" && (
                            <p className="text-xs text-destructive" role="alert">{editError.message}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            onClick={() => saveEdit(d.id, d.domain)}
                            disabled={savingEdit || !editDomain.trim()}
                            aria-label="Save changes"
                          >
                            {savingEdit ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={cancelEdit}
                            disabled={savingEdit}
                            aria-label="Cancel edit"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <Badge variant="destructive">{d.domain}</Badge>
                            <span className="text-xs text-muted-foreground">+ all subdomains</span>
                          </div>
                          {d.reason && <p className="text-xs text-muted-foreground mt-1 truncate">{d.reason}</p>}
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap pt-1">
                          {new Date(d.created_at).toLocaleDateString()}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEdit(d)}
                            disabled={isRemoving || (editingId !== null && editingId !== d.id)}
                            aria-label={`Edit ${d.domain}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemove(d.id, d.domain)}
                            disabled={isRemoving}
                            aria-label={`Remove ${d.domain}`}
                          >
                            {isRemoving ? (
                              <Loader2 className="h-4 w-4 animate-spin text-destructive" />
                            ) : (
                              <Trash2 className="h-4 w-4 text-destructive" />
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!loading && filteredDomains.length > PAGE_SIZE && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-xs text-muted-foreground">
                Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filteredDomains.length)} of {filteredDomains.length}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-xs text-muted-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <BlacklistAuditLog />
    </>
  );
};

const AUDIT_ACTIONS = ["blacklist_domain_added", "blacklist_domain_removed", "blacklist_domain_updated"];

const BlacklistAuditLog = () => {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("admin_logs")
      .select("id, action, created_at, user_id, details")
      .in("action", AUDIT_ACTIONS)
      .eq("entity_type", "blacklisted_domain")
      .order("created_at", { ascending: false })
      .limit(50);

    if (error) {
      // eslint-disable-next-line no-console
      console.error("[BlacklistAuditLog] load failed", error);
      setEntries([]);
      setLoading(false);
      return;
    }

    const rows = (data ?? []) as AuditEntry[];
    const userIds = Array.from(new Set(rows.map((r) => r.user_id).filter(Boolean))) as string[];
    let emailMap: Record<string, string> = {};
    if (userIds.length > 0) {
      const { data: profs } = await supabase.from("profiles").select("id, email").in("id", userIds);
      emailMap = Object.fromEntries((profs ?? []).map((p) => [p.id, p.email]));
    }
    setEntries(rows.map((r) => ({ ...r, actor_email: r.user_id ? emailMap[r.user_id] ?? null : null })));
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const labelFor = (action: string) => {
    if (action === "blacklist_domain_added") return { label: "Added", variant: "default" as const };
    if (action === "blacklist_domain_removed") return { label: "Removed", variant: "destructive" as const };
    return { label: "Updated", variant: "secondary" as const };
  };

  return (
    <Card className="mt-6">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2">
            <History className="h-5 w-5" /> Blacklist Audit Log
          </CardTitle>
          <CardDescription>
            Most recent 50 add/remove/update events, with the admin who made the change.
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} aria-label="Refresh audit log">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-6">No audit entries yet.</p>
        ) : (
          <div className="space-y-2">
            {entries.map((entry) => {
              const { label, variant } = labelFor(entry.action);
              const domain = entry.details?.domain ?? entry.details?.previous_domain ?? "(unknown)";
              return (
                <div key={entry.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 border rounded-md px-3 py-2">
                  <Badge variant={variant} className="w-fit">{label}</Badge>
                  <code className="text-sm font-mono truncate">{domain}</code>
                  <span className="text-xs text-muted-foreground sm:ml-auto truncate">
                    by {entry.actor_email ?? "unknown admin"}
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {new Date(entry.created_at).toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
